import { useMutation } from "@tanstack/react-query";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";

export type ThemePreviewToken = {
  token: string;
  storeSlug: string;
  expiresAt: string;
};

type MintInput = {
  themeId: string;
  versionId: string;
  /** The render-target store whose catalogue fills the theme's product slots. */
  storeSlug: string;
};

/**
 * Mint a short-lived token that renders THIS catalog version in the buyer
 * storefront against `storeSlug`'s catalogue. Nothing is written to that store
 * — see `docs/plans/2026-07-20-admin-theme-studio.md` §1 for why the render
 * target is required at all and why option (c) was chosen over applying the
 * theme to a hidden store.
 *
 * NOT a query: it is a POST with a side effect (minting), it must be
 * re-triggerable on demand (store switch, save, token expiry), and a cached
 * token would be served past its 5-minute TTL.
 */
export function useMintThemePreviewToken() {
  const mutation = useMutation({
    mutationFn: ({ themeId, versionId, storeSlug }: MintInput) =>
      apiJsonClient<ThemePreviewToken>(
        endpoints.admin.themeVersionPreviewToken(themeId, versionId),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ storeSlug }),
        },
      ),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}
